import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Select } from 'app/components';
import { CaretLeft, CloudArrowUp, Info } from '@phosphor-icons/react';
import FileComponent from './fileComponent';
import './style';

const UploadAadharModal = ({
  isOpen,
  onClose,
  uploadTitle,
  uploadFileName1,
  uploadFileName2,
  onSubmit,
  reuploadText1,
  reuploadText2,
  secondaryProofType,
}) => {
  const [internalStep, setInternalStep] = useState(1);
  const [fileTypes, setFileTypes] = useState({ step1: 'pdf', step2: 'pdf' });
  const [panPdfFiles, setPanPdfFiles] = useState([]);
  const [panPngFiles, setPanPngFiles] = useState([null, null]);
  const [secondaryPdfFiles, setSecondaryPdfFiles] = useState([]);
  const [secondaryPngFiles, setSecondaryPngFiles] = useState([null, null]);
  const [uploadError1, setUploadError1] = useState('');
  const [uploadError2, setUploadError2] = useState('');

  const pdfInputRef = useRef(null);
  const pngFrontInputRef = useRef(null);
  const pngBackInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setInternalStep(1);
      setFileTypes({ step1: 'pdf', step2: 'pdf' });
      setPanPdfFiles([]);
      setPanPngFiles([null, null]);
      setSecondaryPdfFiles([]);
      setSecondaryPngFiles([null, null]);
      setUploadError1('');
      setUploadError2('');
    }
  }, [isOpen]);

  const validateFile = (file, allowedTypes) => {
    return file && allowedTypes.includes(file.type);
  };

  const handleFileTypeChange = (type) => {
    setFileTypes((prev) => ({
      ...prev,
      [`step${internalStep}`]: type,
    }));
    setUploadError1('');
    setUploadError2('');
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (validateFile(file, ['application/pdf'])) {
      if (internalStep === 1) {
        setPanPdfFiles([file]);
      } else {
        setSecondaryPdfFiles([file]);
      }
      setUploadError1('');
    } else {
      if (internalStep === 1) {
        setPanPdfFiles([]);
      } else {
        setSecondaryPdfFiles([]);
      }
      setUploadError1('Please upload a valid PDF file');
    }
  };

  const handlePngFrontChange = (e) => {
    const file = e.target.files[0];
    if (validateFile(file, ['image/jpeg', 'image/png'])) {
      if (internalStep === 1) {
        setPanPngFiles((prev) => [file, prev[1] || null]);
      } else {
        setSecondaryPngFiles((prev) => [file, prev[1] || null]);
      }
      setUploadError1('');
    } else {
      if (internalStep === 1) {
        setPanPngFiles((prev) => [null, prev[1] || null]);
      } else {
        setSecondaryPngFiles((prev) => [null, prev[1] || null]);
      }
      setUploadError1('Please upload a valid JPEG or PNG file');
    }
  };

  const handlePngBackChange = (e) => {
    const file = e.target.files[0];
    if (validateFile(file, ['image/jpeg', 'image/png'])) {
      if (internalStep === 1) {
        setPanPngFiles((prev) => [prev[0] || null, file]);
      } else {
        setSecondaryPngFiles((prev) => [prev[0] || null, file]);
      }
      setUploadError2('');
    } else {
      if (internalStep === 1) {
        setPanPngFiles((prev) => [prev[0] || null, null]);
      } else {
        setSecondaryPngFiles((prev) => [prev[0] || null, null]);
      }
      setUploadError2('Please upload a valid JPEG or PNG file');
    }
  };

  const handleReupload = (setter, handler, isBack = false) => (e) => {
    setter((prev) => {
      if (isBack) {
        return [prev[0] || null, null];
      } else if (fileTypes[`step${internalStep}`] === 'pdf') {
        return [];
      } else {
        return [null, prev[1] || null];
      }
    });
    if (e.target.files?.[0]) {
      handler(e);
    }
  };

  const triggerFileInput = (ref) => () => ref.current?.click();

  const handleNext = () => {
    if (internalStep === 1) {
      setInternalStep(2);
      setUploadError1('');
      setUploadError2('');
    }
  };

  const handleBack = () => {
    if (internalStep === 1) {
      onClose();
    } else {
      setInternalStep(1);
      setUploadError1('');
      setUploadError2('');
    }
  };

  const handleSubmit = () => {

    const allFiles = [];

    // Add PAN files based on selected type
    if (fileTypes.step1 === 'pdf') {
      allFiles.push(...panPdfFiles);
    } else {
      allFiles.push(...panPngFiles.filter(Boolean));
    }

    // Add Secondary ID files based on selected type
    if (fileTypes.step2 === 'pdf') {
      allFiles.push(...secondaryPdfFiles);
    } else {
      allFiles.push(...secondaryPngFiles.filter(Boolean));
    }

    const submitData = {
      files: allFiles, 
      panFileType: fileTypes.step1,
      secondaryFileType: fileTypes.step2,
    };

    onSubmit(submitData);
  };

  const FileUploadSection = ({
    label,
    file,
    error,
    fileName,
    reuploadText,
    inputRef,
    onChange,
    onReupload,
    onTrigger,
    accept,
    hint,
    isBack = false,
  }) => (
    <div className="w-full flex flex-col gap-4">
      {label && <p className="text-sm text-grey-70">{label}</p>}
      {!file ? (
        <div
          className="w-full flex flex-col justify-center items-center gap-3 p-4 border border-grey-30 border-dashed rounded-lg cursor-pointer"
          onClick={onTrigger}
        >
          <CloudArrowUp size={16} />
          <div className="heading-16 flex flex-col gap-1 text-center">
            {fileName}
            <span className="text-12 text-grey-70">{hint}</span>
          </div>
          <input
            type="file"
            hidden
            accept={accept}
            onChange={onChange}
            ref={inputRef}
          />
          {error && <div className="text-red-400">{error}</div>}
        </div>
      ) : (
        <div className="flex flex-col items-start">
          <FileComponent
            onDelete={() => {
              if (isBack) {
                if (internalStep === 1) {
                  setPanPngFiles((prev) => [prev[0] || null, null]);
                } else {
                  setSecondaryPngFiles((prev) => [prev[0] || null, null]);
                }
              } else if (fileTypes[`step${internalStep}`] === 'pdf') {
                if (internalStep === 1) {
                  setPanPdfFiles([]);
                } else {
                  setSecondaryPdfFiles([]);
                }
              } else {
                if (internalStep === 1) {
                  setPanPngFiles((prev) => [null, prev[1] || null]);
                } else {
                  setSecondaryPngFiles((prev) => [null, prev[1] || null]);
                }
              }
            }}
            className="w-full max-w-[420px] py-4 px-4 md:py-10 md:px-5"
            file={file}
          />
          <input
            type="file"
            accept={accept}
            hidden
            onChange={onReupload}
            ref={inputRef}
          />
          <Button
            type="tertiary-text"
            className="mt-2"
            onClick={onTrigger}
          >
            <div className="label-14">{reuploadText}</div>
          </Button>
        </div>
      )}
    </div>
  );

  const currentFileType = fileTypes[`step${internalStep}`];
  const currentFiles = internalStep === 1 
    ? (currentFileType === 'pdf' ? panPdfFiles : panPngFiles)
    : (currentFileType === 'pdf' ? secondaryPdfFiles : secondaryPngFiles);
  const currentTitle = internalStep === 1 ? uploadTitle : `Upload your ${secondaryProofType}`;
  const currentFileName1 = internalStep === 1 ? uploadFileName1 : `Upload ${secondaryProofType}`;
  const currentFileName2 = internalStep === 1 ? uploadFileName2 : `Upload ${secondaryProofType}`;
  const currentReuploadText1 = internalStep === 1 ? reuploadText1 : `Re-upload ${secondaryProofType}`;
  const currentReuploadText2 = internalStep === 1 ? reuploadText2 : `Re-upload ${secondaryProofType}`;

  return (
    <Modal
      size="regular"
      isOpen={isOpen}
      onClose={onClose}
      title={(
        <div className="flex flex-col gap-5 heading-24">
          <button className="bg-grey-10 p-2 rounded-full" onClick={handleBack}>
            <CaretLeft size={16} />
          </button>
          <div className="flex flex-col gap-2">
            {currentTitle}
            <div className="text-14">Make sure the images clearly show your face.</div>
            <div className="flex items-center gap-3 bg-[#FFF2F2] pl-3 pr-5 py-2 rounded-full w-full">
              <div className="w-[36px] h-[36px] flex items-center justify-center bg-white rounded-full">
                <Info size={24} color="#FF2459" />
              </div>
              <div className="text-sm font-normal text-primary">Please upload front and back side of your card</div>
            </div>
          </div>
        </div>
      )}
      className="aadhar-modal"
    >
      <div className="flex flex-col items-center w-full h-full gap-8">
        <div className="flex flex-col w-full gap-2">
          <div className="text-sm font-medium self-start">Proof {internalStep} of 2</div>
          <div className='flex gap-2 w-full'>
            <Select
              width="100%"
              label="PDF"
              selected={currentFileType === 'pdf'}
              onClick={() => handleFileTypeChange('pdf')}
            />
            <Select
              width="100%"
              label="PNG"
              selected={currentFileType === 'png'}
              onClick={() => handleFileTypeChange('png')}
            />
          </div>
        </div>

        {currentFileType === 'pdf' ? (
          <FileUploadSection
            label="Proof"
            file={currentFiles[0]}
            error={uploadError1}
            fileName={currentFileName1}
            reuploadText={currentReuploadText1}
            inputRef={pdfInputRef}
            onChange={handlePdfChange}
            onReupload={handleReupload(
              internalStep === 1 ? setPanPdfFiles : setSecondaryPdfFiles,
              handlePdfChange
            )}
            onTrigger={triggerFileInput(pdfInputRef)}
            accept="application/pdf"
            hint="PDF only"
          />
        ) : (
          <>
            <FileUploadSection
              label="Front"
              file={currentFiles[0]}
              error={uploadError1}
              fileName={`${currentFileName1} Front`}
              reuploadText={currentReuploadText1}
              inputRef={pngFrontInputRef}
              onChange={handlePngFrontChange}
              onReupload={handleReupload(
                internalStep === 1 ? setPanPngFiles : setSecondaryPngFiles,
                handlePngFrontChange
              )}
              onTrigger={triggerFileInput(pngFrontInputRef)}
              accept="image/jpeg, image/png"
              hint="JPEG or PNG only"
            />
            <FileUploadSection
              label="Back"
              file={currentFiles[1]}
              error={uploadError2}
              fileName={`${currentFileName2} Back`}
              reuploadText={currentReuploadText2}
              inputRef={pngBackInputRef}
              onChange={handlePngBackChange}
              onReupload={handleReupload(
                internalStep === 1 ? setPanPngFiles : setSecondaryPngFiles,
                handlePngBackChange,
                true
              )}
              onTrigger={triggerFileInput(pngBackInputRef)}
              accept="image/jpeg, image/png"
              hint="JPEG or PNG only"
              isBack={true}
            />
          </>
        )}

        <Button
          type="primary-black"
          children={internalStep === 1 ? 'Next' : 'Submit'}
          disabled={
            (currentFileType === 'pdf' && !currentFiles[0]) ||
            (currentFileType === 'png' && (!currentFiles[0] || !currentFiles[1]))
          }
          onClick={internalStep === 1 ? handleNext : handleSubmit}
          className="w-full"
        />
      </div>
    </Modal>
  );
};

export default UploadAadharModal;
